import { ReactNode, useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ClipboardCheck, LogOut, BookOpen, Menu, MessageSquare } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const nav = [
  { label: "Documents & SOPs", href: "/auditor", icon: BookOpen },
  { label: "Audit Room", href: "/auditor/audit-room", icon: MessageSquare },
];

const AuditorLayout = ({ children }: { children: ReactNode }) => {
  const { user, loading, signOut, isAuditor, fullName } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/auditor-login");
    if (!loading && user && !isAuditor) navigate("/portal");
  }, [user, loading, isAuditor, navigate]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
      </div>
    );
  }
  if (!user || !isAuditor) return null;

  const isActive = (href: string) =>
    href === "/auditor" ? location.pathname === "/auditor" || location.pathname === "/auditor/library" : location.pathname.startsWith(href);

  const NavItems = () => (
    <>
      {nav.map((item) => {
        const Icon = item.icon;
        const active = isActive(item.href);
        return (
          <Link
            key={item.href}
            to={item.href}
            className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all rounded-lg ${
              active
                ? "bg-teal/10 text-teal border-l-2 border-teal pl-[10px]"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            }`}
          >
            <Icon className="w-4 h-4" /> {item.label}
          </Link>
        );
      })}
    </>
  );

  const SidebarBody = () => (
    <>
      <Link to="/auditor" className="flex items-center gap-3 mb-5">
        <img src="/brand/velocity_offshore_full_color.png" alt="Velocity Offshore" className="h-10 w-auto object-contain" />
      </Link>
      <div className="flex items-center gap-3 px-3 py-3 mb-4 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-200">
        <img
          src="/auditor/rmi-seal.jpeg"
          alt="Republic of the Marshall Islands seal"
          className="w-12 h-12 rounded-full object-cover border border-amber-300 shadow-sm shrink-0"
        />
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-1.5">
            <ClipboardCheck className="w-3.5 h-3.5 text-amber-700 shrink-0" />
            <span className="text-[10px] font-bold tracking-[2px] uppercase text-amber-800">Flag State Auditor</span>
          </div>
          <span className="text-[10px] text-amber-700/80">Republic of the Marshall Islands</span>
        </div>
      </div>
      <nav className="flex flex-col gap-1 flex-1"><NavItems /></nav>
      <div className="border-t border-slate-200 pt-4 mt-2">
        <p className="text-xs text-slate-500 truncate mb-2">{fullName || user.email}</p>
        <button
          onClick={() => signOut().then(() => navigate("/auditor-login"))}
          className="flex items-center gap-2 text-xs text-slate-500 hover:text-destructive"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col lg:flex-row">
      <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col p-5 sticky top-0 h-screen">
        <SidebarBody />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-amber-700" />
          <span className="text-xs font-bold tracking-widest text-amber-800 uppercase">Auditor</span>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="p-2 text-slate-600"><Menu className="w-5 h-5" /></button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-white border-slate-200 w-[280px] p-5">
            <SheetHeader>
              <SheetTitle className="text-slate-900">Flag State Auditor</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col h-full pt-4"><SidebarBody /></div>
          </SheetContent>
        </Sheet>
      </div>

      <main className="flex-1 p-5 lg:p-10 max-w-[1400px] mx-auto w-full">{children}</main>
    </div>
  );
};

export default AuditorLayout;
